interface LogEntry {
  level: "debug" | "info" | "warn" | "error";
  message: string;
  context?: string;
  metadata?: Record<string, unknown>;
  timestamp?: string;
  sessionId?: string;
  userAgent?: string;
  url?: string;
}

class ClientLogger {
  private sessionId: string;
  private isEnabled: boolean = true;
  private queue: LogEntry[] = [];
  private isProcessing: boolean = false;
  private maxQueueSize: number = 100;
  private batchSize: number = 10;
  private flushInterval: number = 2000; // 2 seconds
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Generate a session ID for this browser session
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private async sendBatchLogs(logEntries: LogEntry[]): Promise<void> {
    if (!this.isEnabled || logEntries.length === 0) return;

    try {
      // Add session metadata to each log entry
      const logsWithMetadata = logEntries.map((entry) => ({
        ...entry,
        sessionId: this.sessionId,
        userAgent: navigator.userAgent,
        url: window.location.href,
      }));

      const response = await fetch("/api/internal/log", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          logs: logsWithMetadata,
        }),
      });

      if (!response.ok) {
        console.warn("Failed to send logs to server:", response.status);
      }
    } catch (error) {
      // Don't log to console to avoid infinite loops
      // Just silently fail or queue for retry
      console.warn("Failed to send logs to server:", error);
    }
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;

    try {
      // Process logs in batches
      while (this.queue.length > 0) {
        const batch = this.queue.splice(
          0,
          Math.min(this.batchSize, this.queue.length),
        );
        await this.sendBatchLogs(batch);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }

    this.flushTimer = setTimeout(() => {
      this.processQueue();
    }, this.flushInterval);
  }

  private queueLog(logEntry: LogEntry): void {
    if (!this.isEnabled) return;

    this.queue.push(logEntry);

    // Limit queue size to prevent memory issues
    if (this.queue.length > this.maxQueueSize) {
      this.queue.shift(); // Remove oldest log
    }

    // If we've reached batch size, process immediately
    if (this.queue.length >= this.batchSize) {
      if (this.flushTimer) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      this.processQueue();
    } else {
      // Otherwise, schedule a flush
      this.scheduleFlush();
    }
  }

  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  // Manually flush all queued logs
  async flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    await this.processQueue();
  }

  debug(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.queueLog({
      level: "debug",
      message,
      context,
      metadata,
      timestamp: new Date().toISOString(),
    });
  }

  info(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.queueLog({
      level: "info",
      message,
      context,
      metadata,
      timestamp: new Date().toISOString(),
    });
  }

  warn(
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.queueLog({
      level: "warn",
      message,
      context,
      metadata,
      timestamp: new Date().toISOString(),
    });
  }

  error(
    message: string,
    error?: Error,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.queueLog({
      level: "error",
      message,
      context,
      metadata: {
        ...metadata,
        error: error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack,
            }
          : undefined,
      },
      timestamp: new Date().toISOString(),
    });
  }

  // Convenience method for logging user actions
  logUserAction(action: string, metadata?: Record<string, unknown>): void {
    this.info(`User action: ${action}`, "UserAction", {
      action,
      ...metadata,
    });
  }

  // Convenience method for logging page views
  logPageView(page: string, metadata?: Record<string, unknown>): void {
    this.info(`Page view: ${page}`, "PageView", {
      page,
      ...metadata,
    });
  }

  // Convenience method for logging errors
  logError(
    error: Error,
    context?: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.error(error.message, error, context, metadata);
  }

  // Get current session ID
  getSessionId(): string {
    return this.sessionId;
  }
}

// Create and export a singleton instance
export const clientLogger = new ClientLogger();

// Export the class for testing or custom instances
export { ClientLogger };
